import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../api/client'
import { keys } from '../../api/keys'
import type { AsyncState } from '../../platform/async/Async'
import { fromQuery } from '../../platform/async/fromQuery'
import type { components } from '../../api/generated/schema'
import { narrowRole, type Role } from './roles'
import { clearHouseholdId } from './useHousehold'
import { HouseholdError } from './householdError'

/**
 * A member in the shape the screen needs, not the wire's.
 *
 * `role` and `rawRole` are BOTH kept, and that is issue 306 made concrete: the
 * wire types `role` as a bare `string`, so a value outside the enum is
 * possible and the row has to render something. `role` is the narrowed value
 * every permission decision reads — `null` when unrecognised, granting
 * nothing (`roles.ts`). `rawRole` is what the server actually said, so the row
 * can display it rather than a blank or a lie ("Unknown" would be one — the
 * server did not say "unknown", it said a specific string this client has not
 * been taught yet).
 */
export type Member = {
  id: string
  userId: string
  email: string | null
  displayName: string | null
  role: Role | null
  rawRole: string
  joinedAt: string
}

/**
 * The wire row → `Member` transform, on its own so `src/routes/probe/Probe.tsx`'s
 * fixture can call the SAME function `listMembers` does rather than repeating
 * its five lines — issue #329, architect finding N1. Exported for that one
 * caller; it takes no dependency the fixture doesn't already have (it's pure),
 * so this widens nothing beyond "a mapping is reachable outside a mocked
 * fetch".
 */
export function toMember(m: components['schemas']['MembershipResponse']): Member {
  return {
    id: m.id,
    userId: m.user_id,
    email: m.user_email ?? null,
    displayName: m.user_display_name ?? null,
    role: narrowRole(m.role),
    rawRole: m.role,
    joinedAt: m.joined_at,
  }
}

async function listMembers(householdId: string): Promise<Member[]> {
  const { data, error, response } = await api.GET('/api/v1/households/{household_id}/members', {
    params: { path: { household_id: householdId } },
  })
  if (error !== undefined || data === undefined) throw new HouseholdError(error, response.status)
  return data.members.map(toMember)
}

/**
 * The household's members.
 *
 * **No client-side sorting and no status filter.** The server does both:
 * `list_members` filters to `MembershipStatus.ACTIVE` and orders role
 * descending then `joined_at`. Re-sorting here would be a second opinion that
 * drifts from the first the moment either side changes independently.
 *
 * **No empty branch** — no `isEmpty` is passed to `fromQuery`, so this can
 * never report `'empty'`. The signed-in user reading this screen is
 * necessarily one of the rows the server returns; there is no household this
 * query can resolve to zero members for.
 */
export function useMembers(householdId: string | undefined): AsyncState<Member[]> {
  const query = useQuery({
    queryKey: keys.household.members(householdId ?? ''),
    queryFn: () => listMembers(householdId as string),
    enabled: householdId !== undefined,
  })
  return fromQuery(query, {
    message: () => 'Members could not be loaded.',
    recovery: { label: 'Try again', onAct: () => void query.refetch() },
  })
}

/**
 * The signed-in user's own role, which later tasks use to decide what the
 * screen offers (Task 5's role menu, Task 6's remove/leave).
 *
 * **`myRoleOf`, not `useMyRole`** — web-architect review, issue 311, smaller
 * finding 2. Nothing here calls a hook: it is a pure lookup over two values a
 * caller already has in hand, and the `use` prefix told lint (and Task 5's
 * call sites) that it was one, which it is not.
 *
 * Found by matching `myUserId` against `Member.userId` — there is no "my
 * membership" endpoint, and `GET /members` is the only place a role comes
 * back at all. `null` while the members list or the signed-in identity is not
 * yet known, and `null` for a role outside the enum — both grant nothing
 * through `canManageMembers`/`canChangeRole` (`roles.ts`), which is the safe
 * direction for an unresolved permission to fail in.
 */
export function myRoleOf(members: Member[] | undefined, myUserId: string | undefined): Role | null {
  if (members === undefined || myUserId === undefined) return null
  return members.find((m) => m.userId === myUserId)?.role ?? null
}

/**
 * Change a member's role — Task 5, `PATCH /members/{membership_id}`.
 *
 * **Invalidates on settle, not on success.** A failed PATCH may still have
 * committed server-side (a 500 written after the row changed), and the honest
 * recovery is to re-read what the server now says rather than assume the old
 * value still stands. `MemberRow`/`RoleMenu` enforce `canChangeRole`,
 * `isSelf` and `isLastOwner` before ever calling `change` — this mutation is
 * the request itself, not a second opinion about whether it should happen.
 */
export function useChangeRole(householdId: string): {
  change: (membershipId: string, role: Role) => Promise<void>
  isPending: boolean
} {
  const client = useQueryClient()
  const mutation = useMutation({
    mutationFn: async ({ membershipId, role }: { membershipId: string; role: Role }) => {
      const { error, response } = await api.PATCH(
        '/api/v1/households/{household_id}/members/{membership_id}',
        {
          params: { path: { household_id: householdId, membership_id: membershipId } },
          body: { role },
        },
      )
      if (error !== undefined) throw new HouseholdError(error, response.status)
    },
    onSettled: () => {
      void client.invalidateQueries({ queryKey: keys.household.members(householdId) })
    },
  })
  return {
    change: (membershipId, role) => mutation.mutateAsync({ membershipId, role }).then(() => undefined),
    isPending: mutation.isPending,
  }
}

/**
 * Remove a member — Task 6, `DELETE /members/{membership_id}`. **204**, so
 * this resolves to `void`; there is nothing to map through.
 *
 * `remove_member` (`household_service.py:444-456`) sets
 * `status = REMOVED` and commits. Nothing else. **It does NOT revoke
 * account-sharing access** — that premise was wrong in an earlier version of
 * this comment, and the confirmation copy it justified was wrong with it
 * (web-architect review, issue 313, blocking finding B2). `leave_household`
 * (`:492-518`) deletes every `AccountSharingPermission` row for the leaving
 * user under its own `# 2. CRITICAL: Clean up all account sharing
 * permissions`; `remove_member` has no such block. Access is decided by
 * those permission rows directly, not by membership status —
 * `permission_service.py:108-129` and `:152-175` both grant on
 * `AccountSharingPermission.id.isnot(None)` with no membership-status
 * predicate at all. So a removed member drops off this list and out of the
 * household's own view of itself, but keeps whatever account-sharing grants
 * they already had. Filed as `expense-monitor-backend#314`;
 * `MemberActionsMenu`'s confirmation copy says only what is actually true
 * until it lands.
 *
 * **`MemberActionsMenu` enforces `canManageMembers`, `!isSelf` and
 * `isLastOwner` before this is ever called** — this mutation is the request
 * itself, not a second opinion about whether it should happen.
 *
 * **M12-R5: invalidates every owner-filtered query, not only `members`.**
 * M5's and M6's owner pickers derive their options from this list
 * (M12-R11), and a removed person lingering in a filter dropdown is the
 * visible half of the bug. Neither module exists yet, so this invalidates
 * the two roots that will hold them; the comment is here so the next module
 * does not have to rediscover the rule. `onSettled`, matching `useChangeRole`
 * above: a failed DELETE may still have committed server-side, and the
 * honest recovery is to re-read what the server now says.
 */
export function useRemoveMember(householdId: string): {
  remove: (membershipId: string) => Promise<void>
  isPending: boolean
} {
  const client = useQueryClient()
  const mutation = useMutation({
    mutationFn: async (membershipId: string) => {
      const { error, response } = await api.DELETE(
        '/api/v1/households/{household_id}/members/{membership_id}',
        { params: { path: { household_id: householdId, membership_id: membershipId } } },
      )
      if (error !== undefined) throw new HouseholdError(error, response.status)
    },
    onSettled: () => {
      void client.invalidateQueries({ queryKey: keys.household.members(householdId) })
      void client.invalidateQueries({ queryKey: keys.accounts.all() })
      void client.invalidateQueries({ queryKey: keys.transactions.all() })
      void client.invalidateQueries({ queryKey: keys.cashflow.all() })
    },
  })
  return {
    remove: (membershipId: string) => mutation.mutateAsync(membershipId).then(() => undefined),
    isPending: mutation.isPending,
  }
}

/**
 * Leave the household — Task 7 (tracke5-webapp#314),
 * `POST /households/{household_id}/leave`. **204**, so this resolves to
 * `void`.
 *
 * **M12-R12 as restated at intake.** `leave_household`
 * (`household_service.py:492-518`) marks the membership `INACTIVE`, deletes
 * every `AccountSharingPermission` row in BOTH directions (the grants to the
 * leaving user, and the grants from their accounts into the household —
 * `:499-518`), and creates a replacement personal household when the leaving
 * user has no other. So the destination is the viewer's own household, not a
 * setup wizard, and `Household.tsx`'s confirmation says so before it
 * confirms.
 *
 * **The storage clear and the invalidate are split across two different
 * mutation callbacks, and that split is load-bearing** — an earlier version
 * of this hook put both on `onSuccess` alone, which web-architect review
 * (issue 314 fix round, B2) found half wrong:
 *
 *   - **Clearing the stored id belongs on `onSuccess`.** The stored id names
 *     a household the viewer is no longer in ONLY once the server has said
 *     so; clearing it on a REJECTED leave (400 for the last owner) would
 *     detach the viewer's binding from a household they never actually left.
 *   - **Invalidating belongs on `onSettled`**, matching
 *     `useChangeRole`/`useRemoveMember` above, not on `onSuccess` alone. "The
 *     server rejects it outright without touching the membership" is true of
 *     the 400 and false of the failure class those two siblings chose
 *     `onSettled` for: a request that COMMITS server-side but whose response
 *     is lost (a timeout, a dropped connection). `leave_household` is not
 *     free of that risk — the membership really is `INACTIVE` and the
 *     permission rows really are gone in that case, and the screen would go
 *     on rendering a household the viewer has already left, with a member
 *     list and an invitations panel it can no longer legitimately read,
 *     until the next reload happened to re-resolve it. `onSettled` re-reads
 *     regardless of which outcome the client actually observed.
 *
 * `clearHouseholdId()` (`useHousehold.ts`) owns the private-mode try/catch
 * once, for the same reason `writeHouseholdId` does — this module should not
 * hand-roll a second access to the same key (issue 314 fix round, N4).
 *
 * **Invalidates EVERYTHING, with no `queryKey`** — unlike Remove, which
 * narrows to the two owner-filtered roots a removal actually touches. Only
 * the viewer's OWN household binding changes here, but it changes
 * completely: `useHouseholdId` has to re-resolve from scratch (the household
 * list itself is now different), and every query keyed on the old household
 * id — members, invitations, and eventually accounts/transactions — is
 * reading a household the viewer no longer belongs to. A narrower list would
 * have to be kept in sync with every module `/household` ever gains a
 * relationship to; the broad invalidate is the version that cannot go stale.
 *
 * **`MemberActionsMenu`'s Leave item and `Household.tsx`'s footer control
 * both enforce `leaveBlockReason` before this is ever called** — this
 * mutation is the request itself, not a second opinion about whether it
 * should happen.
 */
export function useLeaveHousehold(householdId: string): {
  leave: () => Promise<void>
  isPending: boolean
} {
  const client = useQueryClient()
  const mutation = useMutation({
    mutationFn: async () => {
      const { error, response } = await api.POST('/api/v1/households/{household_id}/leave', {
        params: { path: { household_id: householdId } },
      })
      if (error !== undefined) throw new HouseholdError(error, response.status)
    },
    onSuccess: () => clearHouseholdId(),
    onSettled: () => void client.invalidateQueries(),
  })
  return {
    leave: () => mutation.mutateAsync().then(() => undefined),
    isPending: mutation.isPending,
  }
}
